import { HubSectionType, HubSplitSectionContentType, HubResourceId, HubResourceSection } from 'utils/gloo-types';
import { SortDirection } from 'pages/_AdminAuthoringPages/_components/HubSectionForm';
import { createContext, Dispatch } from 'react';
import { v1 } from 'uuid';
import { Action, ActionType, SplitSectionSide } from './action';
import {
  HubAuthoringFormErrors,
  HubAuthoringFormProperty,
  ClientHubUpsertParams,
  ClientHubSectionUpsertParams,
  ClientHubSection,
} from './types';
import { extractSanitizedHubUpsertParamsFrom } from './utils';

export type Store = {
  hubId: string | null;
  errors: HubAuthoringFormErrors;
  isLoading: boolean;
  isEditMode: boolean;
  isHeaderMinimized: boolean;
  isModalShowing: boolean;
  isDisplayedInDiscover: boolean;
  hubUpsertParams: ClientHubUpsertParams;
  archivedAt?: Date;
};

enum SectionProperties {
  title = 'title',
  description = 'description',
  backgroundColor = 'backgroundColor',
  backgroundImage = 'backgroundImage',
  titleColor = 'titleColor',
  descriptionColor = 'descriptionColor',
  text = 'text',
  // split section properties
  imageUrl = 'imageUrl',
  linkUrl = 'linkUrl',
  textColor = 'textColor',
}

export const initialState: Store = {
  hubId: null,
  errors: {
    sections: {},
  },
  isLoading: false,
  isEditMode: true,
  isHeaderMinimized: false,
  isModalShowing: false,
  isDisplayedInDiscover: false,
  hubUpsertParams: {
    author: '',
    title: '',
    shortDescription: '',
    longDescription: '',
    backgroundColor: '#FFF',
    titleColor: '#000',
    descriptionColor: '#000',
    logoImageUrl: '',
    thumbnailImageUrl: '',
    headerImageUrl: '',
    tags: [],
    visibleInDiscover: false,
    orgTypeFiltering: 'church',
    sections: [],
  },
};

export const HubAuthoringContext = createContext<Store>(null);

export const HubAuthoringDispatchContext = createContext<Dispatch<Action>>(null);

/*
 * The sortSection function is used in the orderSections action and is used to move a section up
 * or down in the sections array and returns a copy of sections array, now sorted.
 */
const sortSections = (
  index: number,
  sortDirection: SortDirection,
  isLast: boolean,
  sections: ClientHubSectionUpsertParams[],
) => {
  const clonedSections = [...sections];
  // no need to sort if only one section
  if (sections.length < 2) return sections;
  if (sortDirection === SortDirection.Up && index > 0) {
    // if were not the first element in the array, swap current section with the one above it
    [clonedSections[index], clonedSections[index - 1]] = [clonedSections[index - 1], clonedSections[index]];
  } else if (sortDirection === SortDirection.Down && !isLast) {
    // if were not the last element in the array, swap current section with the one below it
    [clonedSections[index], clonedSections[index + 1]] = [clonedSections[index + 1], clonedSections[index]];
  }
  return clonedSections;
};

const sortSectionResourceIds = (
  sectionIndex: number,
  resourceIdIndex: number,
  sortDirection: SortDirection,
  isLast: boolean,
  sections: ClientHubSectionUpsertParams[],
) => {
  const cloneSections = [...sections];
  const cloneSection = cloneSections[sectionIndex] as HubResourceSection;
  cloneSection.resourceIds = [...cloneSection.resourceIds];
  // no need to sort if only one resourceId
  if (cloneSection.resourceIds.length < 2) return cloneSections;
  if (sortDirection === SortDirection.Up && resourceIdIndex > 0) {
    // if were not the first element in the array, swap current resourceId with the one above it
    [cloneSection.resourceIds[resourceIdIndex], cloneSection.resourceIds[resourceIdIndex - 1]] = [
      cloneSection.resourceIds[resourceIdIndex - 1],
      cloneSection.resourceIds[resourceIdIndex],
    ];
  } else if (sortDirection === SortDirection.Down && !isLast) {
    // if were not the last element in the array, swap current section with the one below it
    [cloneSection.resourceIds[resourceIdIndex], cloneSection.resourceIds[resourceIdIndex + 1]] = [
      cloneSection.resourceIds[resourceIdIndex + 1],
      cloneSection.resourceIds[resourceIdIndex],
    ];
  }
  return [...cloneSections];
};

const updateSections = (
  index: number,
  updateField: SectionProperties,
  payload: string,
  sections: ClientHubSectionUpsertParams[],
) => {
  const cloneSections = [...sections];
  cloneSections[index][updateField] = payload;
  return cloneSections;
};

const updateSplitSections = (
  index: number,
  side: SplitSectionSide,
  updateField: SectionProperties,
  payload: string,
  sections: ClientHubSectionUpsertParams[],
) => {
  const cloneSections = [...sections];
  cloneSections[index][side][updateField] = payload;
  return cloneSections;
};

const deleteSection = (index: number, sections: ClientHubSectionUpsertParams[]) => {
  const cloneSections = [...sections];
  if (index >= 0 && index < sections.length) {
    cloneSections.splice(index, 1);
    return cloneSections;
  }
  return cloneSections;
};

const addResourceToSection = (index: number, resourceId: HubResourceId, sections: ClientHubSectionUpsertParams[]) => {
  const cloneSections = sections.map((section, i) => {
    if (i === index) {
      return { ...section, resourceIds: [...(section as HubResourceSection).resourceIds, resourceId] };
    }
    return section;
  });
  return cloneSections;
};

const removeSectionResource = (
  sectionIndex: number,
  resourceIdIndex: number,
  sections: ClientHubSectionUpsertParams[],
) => {
  const cloneSections = [...sections];
  const cloneSection = cloneSections[sectionIndex] as HubResourceSection;
  cloneSection.resourceIds = [...cloneSection.resourceIds];

  cloneSection.resourceIds.splice(resourceIdIndex, 1);
  return [...cloneSections];
};

export function reducer(state: Store, action: Action): Store {
  switch (action.type) {
    case ActionType.removeErrors: {
      return {
        ...state,
        errors: {
          sections: {},
        },
      };
    }
    case ActionType.addErrors: {
      const formErrors: HubAuthoringFormErrors[] = action.payload;

      // Reduce the array of FormErrors into an object, replacing the error object
      // that already exist in the state tree.
      const nextErrors = formErrors.reduce(
        (errors: HubAuthoringFormErrors, formError: HubAuthoringFormErrors) => {
          const formProperty = Object.keys(formError)[0] as HubAuthoringFormProperty;

          // Consolidate errors for sections
          if (formProperty === HubAuthoringFormProperty.sections) {
            const sectionId = Object.keys(formError.sections)[0];

            // If a sectionId already exists in the errors.section object, then copy over any
            // existing errors.
            if (sectionId in errors.sections) {
              errors.sections[sectionId] = {
                ...errors.sections[sectionId],
                ...formError.sections[sectionId],
              };
            } else {
              errors.sections[sectionId] = formError.sections[sectionId];
            }

            return errors;
          }

          errors[formProperty] = formError[formProperty];
          return errors;
        },
        { sections: {} },
      );

      return {
        ...state,
        errors: nextErrors,
      };
    }
    case ActionType.setIsLoading: {
      return {
        ...state,
        isLoading: action.isLoading,
      };
    }
    case ActionType.hydrateStore: {
      const sanitizedResponse = extractSanitizedHubUpsertParamsFrom(action.payload);
      return {
        ...state,
        hubUpsertParams: sanitizedResponse,
        archivedAt: action.payload.archivedAt,
      };
    }
    case ActionType.addSection: {
      let section: ClientHubSection;
      const sectionId = v1();

      switch (action.section.type) {
        case HubSectionType.IMAGE: {
          section = {
            clientId: sectionId,
            type: HubSectionType.IMAGE,
            imageUrl: '',
            linkUrl: '',
          };
          break;
        }
        case HubSectionType.TEXT: {
          section = {
            clientId: sectionId,
            type: HubSectionType.TEXT,
            text: '',
          };
          break;
        }
        case HubSectionType.RESOURCE: {
          section = {
            clientId: sectionId,
            type: HubSectionType.RESOURCE,
            resourceIds: [],
          };
          break;
        }
        case HubSectionType.SPLIT: {
          section = {
            clientId: sectionId,
            type: HubSectionType.SPLIT,
            a:
              action.section.a === HubSplitSectionContentType.IMAGE
                ? {
                    type: HubSplitSectionContentType.IMAGE,
                    imageUrl: '',
                  }
                : {
                    type: HubSplitSectionContentType.TEXT,
                    text: '',
                  },
            b:
              action.section.b === HubSplitSectionContentType.IMAGE
                ? {
                    type: HubSplitSectionContentType.IMAGE,
                    imageUrl: '',
                  }
                : {
                    type: HubSplitSectionContentType.TEXT,
                    text: '',
                  },
          };
          break;
        }
        default: {
          console.error('Section was not set: ', section, action.section);
        }
      }

      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: [...state.hubUpsertParams.sections, section],
        },
      };
    }
    case ActionType.deleteSection: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: deleteSection(action.payload.index, state.hubUpsertParams.sections),
        },
      };
    }
    case ActionType.setIsHeaderMinimized: {
      return {
        ...state,
        isHeaderMinimized: !state.isHeaderMinimized,
      };
    }
    case ActionType.toggleIsEditMode: {
      return {
        ...state,
        isEditMode: !state.isEditMode,
      };
    }
    case ActionType.setVisibleInDiscover:
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          visibleInDiscover: action.visibleInDiscover,
        },
      };
    case ActionType.setOrgTypeFiltering:
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          orgTypeFiltering: action.orgTypeFiltering,
        },
      };
    case ActionType.setHubId:
      return {
        ...state,
        hubId: action.hubId,
      };
    case ActionType.orderSections:
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: sortSections(
            action.payload.index,
            action.payload.sortDirection,
            action.payload.isLast,
            state.hubUpsertParams.sections,
          ),
        },
      };
    case ActionType.setSectionTitle: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.title,
            action.payload.title,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionDescription: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.description,
            action.payload.description,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionDescriptionColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.descriptionColor,
            action.payload.descriptionColor,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionTitleColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.titleColor,
            action.payload.titleColor,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionBackgroundColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.backgroundColor,
            action.payload.backgroundColor,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionBackgroundImage: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.backgroundImage,
            action.payload.backgroundImage,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setTitleColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          titleColor: action.payload,
        },
      };
    }
    case ActionType.setDescriptionColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          descriptionColor: action.payload,
        },
      };
    }
    case ActionType.setBackgroundColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          backgroundColor: action.payload,
        },
      };
    }
    case ActionType.setTitle: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          title: action.payload,
        },
      };
    }
    case ActionType.setAuthor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          author: action.payload,
        },
      };
    }
    case ActionType.setShortDescription: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          shortDescription: action.payload,
        },
      };
    }
    case ActionType.setLongDescription: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          longDescription: action.payload,
        },
      };
    }
    case ActionType.setThumbnailImageUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          thumbnailImageUrl: action.payload,
        },
      };
    }
    case ActionType.setLogoImageUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          logoImageUrl: action.payload,
        },
      };
    }
    case ActionType.setHeaderImageUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          headerImageUrl: action.payload,
        },
      };
    }
    case ActionType.addSectionResource: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: addResourceToSection(
            action.payload.index,
            action.payload.hubResourceId,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionText: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.text,
            action.payload.text,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionTextColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.textColor,
            action.payload.textColor,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionImageUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.imageUrl,
            action.payload.imageUrl,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSectionLinkUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSections(
            action.payload.index,
            SectionProperties.linkUrl,
            action.payload.linkUrl,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSplitSectionText: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSplitSections(
            action.payload.index,
            action.payload.side,
            SectionProperties.text,
            action.payload.text,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSplitSectionTextColor: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSplitSections(
            action.payload.index,
            action.payload.side,
            SectionProperties.textColor,
            action.payload.textColor,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSplitSectionDestinationLinkUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSplitSections(
            action.payload.index,
            action.payload.side,
            SectionProperties.linkUrl,
            action.payload.linkUrl,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.setSplitSectionImageUrl: {
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: updateSplitSections(
            action.payload.index,
            action.payload.side,
            SectionProperties.imageUrl,
            action.payload.imageUrl,
            state.hubUpsertParams.sections,
          ),
        },
      };
    }
    case ActionType.orderSectionResourceIds:
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: sortSectionResourceIds(
            action.payload.sectionIndex,
            action.payload.resourceIdIndex,
            action.payload.sortDirection,
            action.payload.isLast,
            state.hubUpsertParams.sections,
          ),
        },
      };
    case ActionType.deleteSectionResourceId:
      return {
        ...state,
        hubUpsertParams: {
          ...state.hubUpsertParams,
          sections: removeSectionResource(
            action.payload.sectionIndex,
            action.payload.resourceIdIndex,
            state.hubUpsertParams.sections,
          ),
        },
      };
    case ActionType.setArchivedAt:
      return {
        ...state,
        archivedAt: action.archivedAt,
        hubUpsertParams: action.archivedAt
          ? {
              ...state.hubUpsertParams,
              visibleInDiscover: false,
            }
          : state.hubUpsertParams,
      };
    default:
      throw new Error(`No branch for action type: ${(action as any).type}`);
  }
}
